import axios from "axios";
import { toast } from "react-toastify";
import { APP_VERSION } from "../version";
import { clearAppCache } from "../utils/clearAppCache";

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
let url;
console.log(process.env);
if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
  url = "http://localhost:8000";
} else {
  url = "https://memiz.mizoram.gov.in/v1";
  //  url = "http://localhost:8000";
  // url = "https://api.memiz.in";
}

// cdn for iamges
const cdn = "https://memiz.blr1.cdn.digitaloceanspaces.com";
const api = axios.create({ baseURL: url });

// Attach the hardcoded frontend build version (src/version.js) to every
// request, so the backend's App\Http\Middleware\CheckApiVersion can compare
// it against config('app.version') (APP_VERSION in the Laravel .env) and
// catch an already-open, stale browser tab from before the last deploy.
api.interceptors.request.use((config) => {
  config.headers["X-App-Version"] = APP_VERSION;
  return config;
});

// Which server version we've already force-reloaded for, so a deploy that's
// out of sync between frontend and backend (new backend live, but this exact
// stale bundle still being served — a CDN/proxy caching issue, or the two
// halves simply deployed at different times) can't reload forever: one
// attempt per distinct server version, then stop and just tell the user.
// sessionStorage (not localStorage) so a fresh tab always gets its own first
// attempt rather than inheriting a stuck flag from a previous session.
const VERSION_RELOAD_KEY = "memiz_version_reload_attempted_for";

const alreadyReloadedFor = (serverVersion) => {
  try {
    return sessionStorage.getItem(VERSION_RELOAD_KEY) === String(serverVersion);
  } catch {
    // Some private-mode/embedded-webview contexts throw on storage access —
    // treat as "not yet attempted" so the reload still gets one try.
    return false;
  }
};

const markReloadedFor = (serverVersion) => {
  try {
    sessionStorage.setItem(VERSION_RELOAD_KEY, String(serverVersion));
  } catch {
    // Nothing to do if storage is unavailable — worst case this specific tab
    // loses the loop guard, it doesn't lose the reload itself.
  }
};

// Global response interceptor: 503 shows a maintenance toast; a 409 carrying
// version_mismatch means CheckApiVersion caught this exact tab running a
// stale build — clear its cache and reload to pick up the current one, the
// same routine as the manual "Clear app cache" menu item in MainLayout.jsx.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 503) {
      toast.error("Site Under Maintenance. Please read the announcement from the site administrator from appropriate channels",{toastId:"maintenance-toast",autoClose: false});
    }
    if (status === 409 && error?.response?.data?.version_mismatch) {
      const serverVersion = error.response.data.server_version;
      if (!alreadyReloadedFor(serverVersion)) {
        markReloadedFor(serverVersion);
        toast.error("A new version of MEMiz is available. Refreshing…", {
          toastId: "version-mismatch-toast",
          autoClose: false,
        });
        clearAppCache()
          .catch(() => {}) // best-effort — reload regardless of whether clearing itself succeeded
          .finally(() => window.location.reload());
      } else {
        // Already tried once for this exact server version and we're still
        // seeing the mismatch — reloading again won't help (the stale bundle
        // is still what's being served), so stop looping and say so instead.
        toast.error(
          "This app can't load a compatible version even after refreshing. Please try again shortly or contact support.",
          { toastId: "version-mismatch-stuck-toast", autoClose: false }
        );
      }
    }
    return Promise.reject(error);
  }
);
// A handful of VerifyController methods (verifyVerificationStaff,
// verifyEscalationReview, verifyCorrectionReview, verifyRestoreReview) return
// {"error": "..."} from their own try/catch blocks, instead of the
// {"message": "..."} shape Laravel's default exception handler — and most other
// controllers — use. A caller that only reads .message sees a blank/generic
// toast on exactly those failures (confirmed live: this is why rejecting a CS08
// escalation looked like "nothing happens" — the real error, an FCM push
// failure, never reached the user). Read whichever key is present, in that
// order, before falling back to a generic message, so no failure ever renders
// a blank toast.
const getErrorMessage = (err, fallback = "Something went wrong. Please try again.") =>
  err?.response?.data?.error || err?.response?.data?.message || fallback;

export { url, api, axios, cdn, getErrorMessage };
